// src/pages/Staff.js
import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import "./Staff.css";
import { useConfirm } from "../components/ConfirmModal";
import { maskPhone } from "../utils/phone";
import { getStaff, softDeleteStaff, restoreStaff, hardDeleteStaff, downloadFile, apiUrl } from "../utils/api";


/* === 페이지 내부 모달 (닫기 가능 / 로딩 스피너 지원) === */
function Modal({
  open,
  title = "알림",
  message,
  loading = false,
  danger = false,
  closable = true,
  onClose,
  children,
}) {
  if (!open) return null;
  const tryClose = () => { if (closable && onClose) onClose(); };

  return (
    <div className="modal-backdrop" onClick={tryClose}>
      <div
        className={`modal-panel${danger ? " danger" : ""}${loading ? " loading" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="modal-head"><h3>{title}</h3></header>
        <div className="modal-body">
          {loading ? (
            <div className="loading-box">
              <div className="modal-spinner" />
              <p>서버에서 데이터를 불러오는 중입니다…</p>
            </div>
          ) : (
            <>
              {message && <p>{message}</p>}
              {children}
            </>
          )}
        </div>
        <footer className="modal-foot">
          <button className="btn" onClick={tryClose}>닫기</button>
        </footer>
      </div>
    </div>
  );
}

// 화면이 "모바일 레이아웃(<=640px)"이면 true
function useIsMobile() {
  const get = () => {
    if (typeof window === "undefined") return false;
    // 폭 기반 미디어쿼리: 실제 레이아웃 브레이크포인트와 동일하게
    const mq = window.matchMedia?.("(max-width: 640px)");
    if (mq && typeof mq.matches === "boolean") return mq.matches;

    // matchMedia 미지원 대비
    const w = Math.min(window.innerWidth || 0, document.documentElement?.clientWidth || Infinity);
    return w <= 640;
  };

  const [isMobile, setIsMobile] = React.useState(get);

  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(max-width: 640px)");
    const onChange = (e) => setIsMobile(e.matches);

    // 최신/구형 브라우저 겸용
    if (mql.addEventListener) mql.addEventListener("change", onChange);
    else mql.addListener(onChange);

    // 초기에 한 번 보정
    setIsMobile(mql.matches);

    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", onChange);
      else mql.removeListener(onChange);
    };
  }, []);

  return isMobile;
}

export default function Staff() {
  const isMobile = useIsMobile();
  const abortRef = useRef(null);
  const { confirm, ConfirmDialog } = useConfirm();

  const isEmpty = (v) => v == null || (typeof v === "string" && v.trim() === "");
  const onlyDigits = (s = "") => String(s).replace(/\D/g, "");
  const normLoose = (s = "") => String(s).toLowerCase().replace(/\s+/g, "");
  const sanitizePhone = (s = "") => String(s).replace(/[^0-9-]/g, "");

  /* === 토큰/매칭 유틸 (전 필드 공통) === */
  const tokenize = (q = "") =>
    (String(q).match(/[0-9]+|[a-zA-Z]+|[가-힣]+/g) || []).map(t => t.trim()).filter(Boolean);

  // 비연속 서브시퀀스(글자 건너뛰기 허용)
  const containsSubsequence = (hay = "", needle = "") => {
    if (!needle) return true;
    let i = 0;
    const H = String(hay);
    const N = String(needle);
    for (let k = 0; k < H.length && i < N.length; k++) {
      if (H[k] === N[i]) i++;
    }
    return i === N.length;
  };

  const tokenMatchInText = useCallback((textRaw, query) => {
    const tokens = tokenize(query);
    if (!tokens.length) return true;

    const text = String(textRaw ?? "");
    const textNorm = text.toLowerCase().replace(/\s+/g, "");
    const textDigits = text.replace(/\D/g, "");

    return tokens.every((t0) => {
      const t = t0.toLowerCase();

      if (/^a\.?m\.?$/.test(t)) return /오전|\bam\b/i.test(text);
      if (/^p\.?m\.?$/.test(t)) return /오후|\bpm\b/i.test(text);

      if (/^\d+$/.test(t)) return containsSubsequence(textDigits, t);
      const tNorm = t.replace(/\s+/g, "");
      return containsSubsequence(textNorm, tNorm);
    });
  }, []);

  // "-" 처리 + 토큰 AND(서브시퀀스) 매칭
  const matchesDashOrIncludes = useCallback((value, query) => {
    const q = String(query || "").trim();
    if (!q) return true;
    if (q === "-") return isEmpty(value) || String(value).trim() === "-";
    return tokenMatchInText(value ?? "", q);
  }, [tokenMatchInText]);

  const matchesPhone = useCallback((phoneValue, query) => {
    const q = String(query || "").trim();
    if (!q) return true;
    if (q === "-") return isEmpty(phoneValue) || String(phoneValue).trim() === "-";
    return tokenMatchInText(phoneValue ?? "", q);
  }, [tokenMatchInText]);

  /* ===== 입력 폼 상태 ===== */
  const [isAdding, setIsAdding] = useState(false);
  const [form, setForm] = useState({ username: "", password: "", phone: "", name: "" });
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [pwError, setPwError] = useState("");
  const [usernameError, setUsernameError] = useState("");
  const [nameError, setNameError] = useState("");
  const [submitTip, setSubmitTip] = useState(""); // 확인 버튼 위 말풍선

  /* ===== 목록/로딩/모달 상태 ===== */
  const [data, setData] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);

  const [showDeleted, setShowDeleted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lastError, setLastError] = useState(null);
  const [modal, setModal] = useState({
    open: false, title: "", message: "", loading: false, danger: false, closable: true
  });

  const [warningFor, setWarningFor] = useState(null);
  const [hiddenDeletedIds] = useState([]); // 휴지통 화면에서 숨길 로컬 ID (지금은 미사용)

  const [authExpired, setAuthExpired] = useState(false); //  세션만료 플래그
  

  /* ===== 검색 상태 ===== */
  const EMPTY_Q = useMemo(
    () => ({ username: "", password: "", phone: "", name: "" }),
    []
  );
  const [draft, setDraft] = useState(() => ({ ...EMPTY_Q }));
  const [q, setQ]       = useState(() => ({ ...EMPTY_Q }));
  const isFilterActive = useMemo(
    () => Object.values(q).some(v => String(v ?? "").trim() !== ""),
    [q]
  );

  const applySearch = () => { setQ(draft); setPage(1); };
  const clearSearch = () => { setDraft({ ...EMPTY_Q }); };
  const showAll = useCallback(() => {
    setDraft({ ...EMPTY_Q });
    setQ({ ...EMPTY_Q });
    setPage(1);
  }, [EMPTY_Q]);

  /* ===== 검색 입력 좌/우 화살표 ===== */
  const searchRefs = useRef([]); // [username, password, phone, name]
  const handleArrowNav = (e, idx) => {
    const el = e.target;

    // ── 모바일: ↑/↓ 로 위/아래 칸 이동 ───────────────────────────
    if (isMobile) {
      if (e.key === "ArrowUp" && idx > 0) {
        e.preventDefault();
        const prev = searchRefs.current[idx - 1];
        if (prev) {
          prev.focus();
          const len = prev.value.length;
          requestAnimationFrame(() => prev.setSelectionRange?.(len, len));
        }
      }
      if (e.key === "ArrowDown" && idx < searchRefs.current.length - 1) {
        e.preventDefault();
        const next = searchRefs.current[idx + 1];
        if (next) {
          next.focus();
          requestAnimationFrame(() => next.setSelectionRange?.(0, 0));
        }
      }
      return; // 모바일은 여기서 끝
    }

    // ── 데스크톱: 기존 ←/→ (커서가 맨앞/맨끝일 때만 이동) ───────────
    const posStart = el.selectionStart ?? 0;
    const posEnd = el.selectionEnd ?? 0;
    const atStart = posStart === 0 && posEnd === 0;
    const atEnd = posStart === el.value.length && posEnd === el.value.length;


    if (e.key === "ArrowLeft" && atStart && idx > 0) {
      e.preventDefault();
      const prev = searchRefs.current[idx - 1];
      if (prev) {
        prev.focus();
        const len = prev.value.length;
        requestAnimationFrame(() => prev.setSelectionRange?.(len, len));
      }
    }
    if (e.key === "ArrowRight" && atEnd && idx < searchRefs.current.length - 1) {
      e.preventDefault();
      const next = searchRefs.current[idx + 1];
      if (next) {
        next.focus();
        requestAnimationFrame(() => next.setSelectionRange?.(0, 0));
      }
    }
  };

  /* ===== 엔터 제출 ===== */
  const hasMeaningfulQuery = useCallback((d) => {
    const anyText = [d.username, d.password, d.name].some(v => normLoose(v).length > 0);
    const dashInText = [d.username, d.password, d.name].some(v => String(v).trim() === "-");
    const phoneMeaningful = onlyDigits(d.phone).length > 0 || String(d.phone).trim() === "-";
    return anyText || dashInText || phoneMeaningful;
  }, []);

  const onSubmitSearch = (e) => {
    e.preventDefault();
    if (hasMeaningfulQuery(draft)) {
      applySearch();
    } else {
      // 모든 입력이 비어있으면 '전체 보기' 실행
      showAll();
    }
  };

  /* ===== 페이지네이션 상태 ===== */
  const PAGE_SIZE = 50;
  const [page, setPage] = useState(1);

  /* ===== Pagination 컴포넌트 ===== */

  const Pagination = ({ page, totalPages, total, setPage }) => {
    const totalPagesSafe = Math.max(1, totalPages);

    // 점프 입력값
    const [jump, setJump] = useState(String(page));
    useEffect(() => {
      const next = String(page);
      setJump(prev => (prev === next ? prev : next));
    }, [page]);

    const clamp = (n) =>
      Math.min(totalPagesSafe, Math.max(1, Number.isFinite(n) ? Math.trunc(n) : 1));

    const goFirst = () => setPage(1);
    const goPrev  = () => setPage((p) => Math.max(1, p - 1));
    const goNext  = () => setPage((p) => Math.min(totalPagesSafe, p + 1));
    const goLast  = () => setPage(totalPagesSafe);

    const tryJump = () => {
      const n = Number(jump);
      if (!Number.isFinite(n)) return;
      setPage(clamp(n));
    };
    const onJumpKey = (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        tryJump();
      }
    };

    const disablePrev = page <= 1;
    const disableNext = page >= totalPagesSafe;

    if (!total) return null;

    return (
      <div className="pagination" role="navigation" aria-label="페이지네이션">
        <div className="pager">
          <button
            className="pg-btn pg-first"
            title="맨 처음"
            aria-label="맨 처음 페이지"
            onClick={goFirst}
            disabled={disablePrev}
          />
          <button className="pg-btn" title="이전" aria-label="이전 페이지"
                  onClick={goPrev} disabled={disablePrev}>◀</button>

          <button className="pager-current" disabled aria-current="page">
            {page}
          </button>

          <button className="pg-btn" title="다음" aria-label="다음 페이지"
                  onClick={goNext} disabled={disableNext}>▶</button>
          <button
            className="pg-btn pg-last"
            title="맨 끝"
            aria-label="맨 끝 페이지"
            onClick={goLast}
            disabled={disableNext}
          />

          <div className="pg-jump">
            <input
              className="pg-input"
              type="number"
              min={1}
              max={totalPagesSafe}
              value={jump}
              onChange={(e) => setJump(e.target.value)}
              onKeyDown={onJumpKey}
              aria-label="이동할 페이지 입력"
            />
            <button className="pg-go" onClick={tryJump}>이동</button>
          </div>
        </div>

        <div className="page-info">
          페이지 {page}/{totalPagesSafe} · 총 {total.toLocaleString()}건
        </div>
      </div>
    );
  };

  /* ===== 목록 조회 ===== */
  const fetchData = async (deleted = false) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      setLoading(true);
      setLastError(null);
      // ↓↓↓ 여기만 변경: utils/api 사용
      let list = await getStaff(deleted, { signal: controller.signal });
      if (!Array.isArray(list)) list = [];
      const sorted = [...list].sort((a, b) => a.id - b.id);
      setData(sorted);
      setPage(1);
      setAuthExpired(false); //  성공 시 세션 만료 해제
    }  catch (e) {
  if (e.name !== "AbortError") {
    console.error("[Staff] load error", e);
    setLastError(e);
    setData([]);

    // 세션 만료 여부 추정(Cloudflare Access CORS/리다이렉트 포함)
    const msg = String(e?.message || "");
    const status = e?.status || e?.response?.status;
    const code = e?.code || e?.response?.data?.code;
    const looksLikeAccessRedirect =
      msg.includes("Failed to fetch") ||
      msg.includes("TypeError") ||
      msg.toLowerCase().includes("networkerror");

    if (
      code === "AUTH_EXPIRED" ||
      [401, 403, 419, 440].includes(Number(status)) ||
      looksLikeAccessRedirect
    ) {
      setAuthExpired(true);
    } else {
      setAuthExpired(false);
    }
  }
}
 finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    return () => abortRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ===== 다운로드 모달 상태 전환 ===== */
  useEffect(() => {
    if (!modal.open || !modal.loading) return;
    if (loading) return;
    if (lastError) {
      setModal({
        open: true, title: "다운로드 불가",
        message: "서버 오류로 목록을 불러오지 못했습니다.",
        loading: false, danger: true, closable: true
      });
    } else if (!data.length) {
      setModal({
        open: true, title: "다운로드 불가",
        message: "표시할 데이터가 없어 다운로드할 수 없습니다.",
        loading: false, danger: true, closable: true
      });
    } else {
      setModal({
        open: true, title: "불러오기 완료",
        message: "데이터 불러오기 완료했습니다. 다시 ‘엑셀 다운로드’ 버튼을 눌러주세요.",
        loading: false, danger: false, closable: true
      });
    }
  }, [loading, modal.open, modal.loading, lastError, data.length]);

  /* ===== 데이터/뷰 변경 시 페이지 보정 ===== */
  useEffect(() => {
    const baseRowsX = showDeleted ? data.filter((r) => !hiddenDeletedIds.includes(r.id)) : data;
    const totalPagesX = Math.max(1, Math.ceil(baseRowsX.length / PAGE_SIZE));
    setPage(prev => (prev > totalPagesX ? totalPagesX : prev));
  }, [data, showDeleted, hiddenDeletedIds, PAGE_SIZE]);

  /* ===== 체크박스/삭제/복원/완전삭제 ===== */
  const handleCheckbox = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleDelete = async () => {
    if (selectedRows.length === 0) { setWarningFor("delete"); setTimeout(() => setWarningFor(null), 1800); return; }
    const ok = await confirm({ title: "삭제 확인", message: `선택한 직원 ${selectedRows.length}건을 삭제할까요?`, danger: true });
    if (!ok) return;
    try {
      await softDeleteStaff(selectedRows);
      setData(prev => prev.filter(r => !selectedRows.includes(r.id)));
      setSelectedRows([]);
    } catch (e) {
      console.error(e); alert("삭제에 실패했습니다.");
    }
  };

  // 복원
  const handleRestore = async () => {
    if (selectedRows.length === 0) { setWarningFor("restore"); setTimeout(() => setWarningFor(null), 1800); return; }
    const ok = await confirm({ title: "복원 확인", message: `선택한 직원 ${selectedRows.length}건을 복원할까요?` });
    if (!ok) return;
    try {
      await restoreStaff(selectedRows);
      setData(prev => prev.filter(r => !selectedRows.includes(r.id)));
      setSelectedRows([]);
    } catch (e) {
      console.error(e); alert("복원에 실패했습니다.");
    }
  };

  // 영구 삭제
  const handleHardDelete = async () => {
    if (selectedRows.length === 0) { setWarningFor("hard"); setTimeout(() => setWarningFor(null), 1800); return; }
    const ok = await confirm({ title: "영구 삭제 확인", message: <>선택한 직원 {selectedRows.length}건을 영구 삭제 할까요?<br className="br-392" /><span className="nowrap-attach">(복구 불가)</span></> , danger: true});
    if (!ok) return;
    try {
      await hardDeleteStaff(selectedRows);
      setData(prev => prev.filter(r => !selectedRows.includes(r.id)));
      setSelectedRows([]);
    } catch (e) {
      console.error(e); alert("완전 삭제에 실패했습니다.");
    }
  };

  const handleToggleView = () => {
    const next = !showDeleted;
    setShowDeleted(next);
    setSelectedRows([]);       // 토글 시 선택 초기화
    setPage(1);                // 첫 페이지로
    fetchData(next);
  };

  /* ===== CSV 내보내기 유틸 ===== */
  const csvEscape = (v) => {
    const s = v == null ? "" : String(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  // 모든 셀을 텍스트로 강제 (="…")
  const toExcelText = (v) => {
    const s = (v == null || (typeof v === "string" && v.trim() === "")) ? "-" : String(v);
    return `="${s.replace(/"/g, '""')}"`;
  };

  // 기존 kstFilenameTimestamp() 자리에 그대로 교체
  const kstFilenameTimestamp = () => {
    const parts = new Intl.DateTimeFormat("ko-KR", {
      timeZone: "Asia/Seoul",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "numeric",       // 12시간제
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    }).formatToParts(new Date());

    const get = (t) => parts.find(p => p.type === t)?.value;
    const year = get("year");
    const month = get("month");
    const day = get("day");
    const dp = get("dayPeriod") || "";   // "오전" / "오후"
    const hour = get("hour");            // 1~12
    const minute = get("minute")?.padStart(2, "0");
    const second = get("second")?.padStart(2, "0");

    // 파일명에 쓰기 좋게 공백 대신 언더스코어 사용
    return `${year}-${month}-${day}_${dp}_${hour}시 ${minute}분 ${second}초`;
  };

  const downloadCSV = (filename, headers, rows) => {
    const lines = [];
    lines.push(headers.map(csvEscape).join(","));
    rows.forEach((r) => lines.push(r.map(csvEscape).join(",")));
    const blob = new Blob(
      ["\ufeff" + lines.join("\r\n")],
      { type: "text/csv;charset=utf-8" }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      URL.revokeObjectURL(url);
      a.remove();
    }, 0);
  };

  /* ===== 기본/필터/페이지 ===== */
  const baseRows = showDeleted
    ? data.filter((r) => !hiddenDeletedIds.includes(r.id))
    : data;

  const filtered = baseRows.filter(r => {
    if (q.username && !matchesDashOrIncludes(r.username, q.username)) return false;
    if (q.password && !matchesDashOrIncludes(r.password, q.password)) return false; // 해시 포함 검색
    if (q.phone    && !matchesPhone(r.phone, q.phone)) return false;
    if (q.name     && !matchesDashOrIncludes(r.name, q.name)) return false;
    return true;
  });

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const startIdx = (page - 1) * PAGE_SIZE;
  const rowsToRender = filtered.slice(startIdx, startIdx + PAGE_SIZE);

  /* ===== 다운로드 버튼 ===== */
  const handleDownload = () => {
    if (loading) {
      setModal({
        open: true, title: "불러오는 중…",
        message: "서버에서 데이터를 불러오는 중입니다.\n완료 후 안내를 확인해 주세요.",
        loading: true, danger: false, closable: true
      });
      return;
    }
    if (lastError) {
      setModal({
        open: true, title: "다운로드 불가",
        message: "서버 오류로 목록을 불러오지 못했습니다.",
        loading: false, danger: true, closable: true
      });
      return;
    }
    if (!data.length) {
      setModal({
        open: true, title: "다운로드 불가",
        message: "표시할 데이터가 없어 다운로드할 수 없습니다.",
        loading: false, danger: true, closable: true
      });
      return;
    }

    const headers = ["번호","아이디","비밀번호","전화번호","이름"];

    // 1) 검색 중
    if (isFilterActive) {
      const rows = filtered.map((row, idx) => [
        toExcelText(idx + 1),
        toExcelText(row.username ?? "-"),
        toExcelText(row.password ?? "-"),
        toExcelText(maskPhone(row.phone)),
        toExcelText(row.name ?? "-")
      ]);
      const fname = `직원_${showDeleted ? "휴지통_" : ""}검색결과_${kstFilenameTimestamp()}.csv`;
      downloadCSV(fname, headers, rows);
      return;
    }

    // 2) 검색 아님
    if (showDeleted) {
      const rows = baseRows.map((row, idx) => [
        toExcelText(idx + 1),
        toExcelText(row.username ?? "-"),
        toExcelText(row.password ?? "-"),
        toExcelText(maskPhone(row.phone)),
        toExcelText(row.name ?? "-")
      ]);
      const fname = `직원_휴지통_전체_${kstFilenameTimestamp()}.csv`;
      downloadCSV(fname, headers, rows);
    } else {
      const fname = `직원_전체_${kstFilenameTimestamp()}.xlsx`;
      downloadFile(`/api/staff/download`, fname).catch(() => {
        setModal({
          open: true,
          title: "다운로드 실패",
          message: "파일을 내려받지 못했습니다. 잠시 후 다시 시도해 주세요.",
          loading: false,
          danger: true,
          closable: true
        });
      });
    }

  };

  /* ===== 입력 폼 핸들러 ===== */
  const startAdd   = () => {
    setForm({ username: "", password: "", phone: "", name: "" });
    setErrorMsg(""); setPhoneError(""); setPwError("");
    setUsernameError(""); setNameError("");
    setSubmitTip("");
    setIsAdding(true);
  };
  const cancelAdd  = () => {
    setIsAdding(false);
    setErrorMsg("");
    setPhoneError("");
    setPwError("");
    setUsernameError("");
    setNameError("");
    setSubmitTip("");
  };
  const onChange   = (e) => {
    const { name, value } = e.target;
    const next = name === "password" ? value.slice(0, 20) : value;

     // 전화번호: 숫자/하이픈만 허용 + 잘못된 입력 시 경고
    if (name === "phone") {
      const cleaned = sanitizePhone(next);
      setForm((f) => ({ ...f, [name]: cleaned }));
      if (cleaned !== next) {
        setPhoneError("숫자와 하이픈(-)만 입력 가능합니다.");
      } else {
        setPhoneError("");
        setErrorMsg("");
      }
      setSubmitTip("");
      return;
    }

    setForm((f) => ({ ...f, [name]: next }));
    if (name === "phone")    { setPhoneError(""); setErrorMsg(""); }
    if (name === "password") { setPwError("");   setErrorMsg(""); }
    if (name === "username") { setUsernameError(""); setErrorMsg(""); }
    if (name === "name")     { setNameError(""); setErrorMsg(""); }
    setSubmitTip("");
  };

  const validateAll = () => {
    let phoneErr = "";
    let pwErr = "";
    let commonErr = "";

    const username = (form.username || "").trim();
    const name     = (form.name || "").trim();
    const pwRaw    = (form.password || "").trim();
    const phoneRaw = String(form.phone || "");
    const phoneDigits = onlyDigits(phoneRaw);

    // 1) 전부 비어있음
    if (!username && !pwRaw && !phoneDigits && !name) {
      return { ok: false, phoneErr: "", pwErr: "", commonErr: "내용을 입력하세요." };
    }

    // 2) 일부만 비어있음
    if (!username || !pwRaw || !phoneDigits || !name) {
      return { ok: false, phoneErr: "", pwErr: "", commonErr: "모든 칸을 채워주세요." };
    }

    // === 여기서부터는 "모든 칸이 채워진 경우"에만 세부 검증 ===
    // 아이디/이름 기본 체크
    if (!username) commonErr ||= "아이디를 입력하세요.";
    if (!name)     commonErr ||= "이름을 입력하세요.";

    // 비밀번호 길이 체크
    if (!pwRaw) {
      commonErr ||= "비밀번호를 입력하세요.";
    } else {
      const len = pwRaw.length;
      if (len < 8) pwErr = "비밀번호는 최소 8자 입니다.";
      else if (len > 20) pwErr = "비밀번호는 최대 20자 입니다.";
    }

    // 전화번호 숫자 길이 체크
    let digits = phoneDigits;
    if (!digits) {
      phoneErr = "전화번호를 입력하세요.";
    } else {
      if (digits.startsWith("82")) digits = "0" + digits.slice(2);
      if (digits.length <= 3) phoneErr = "전화번호가 너무 짧습니다.";
      else if (digits.startsWith("010")) {
        if (digits.length < 11)      phoneErr = "휴대폰 번호가 너무 짧습니다. 010은 11자리입니다.";
        else if (digits.length > 11) phoneErr = "휴대폰 번호가 너무 깁습니다. 010은 11자리입니다.";
      } else if (digits.length > 11) phoneErr = "전화번호가 너무 깁습니다.";
    }

    return { ok: !(phoneErr || pwErr || commonErr), phoneErr, pwErr, commonErr };
  };

  const submitAdd  = async () => {
    const v = validateAll();

    // 일단 모두 초기화
    setUsernameError("");
    setNameError("");
    setPhoneError("");
    setPwError("");
    setErrorMsg("");
    setSubmitTip("");

    if (!v.ok) {
      const username = (form.username || "").trim();
      const name     = (form.name || "").trim();
      const pwRaw    = (form.password || "").trim();
      const phoneDigits = onlyDigits(String(form.phone || ""));

      // 1) 전부 비어있음 → 이름 칸에만 표시
      if (v.commonErr === "내용을 입력하세요.") {
        setSubmitTip("내용을 입력하세요.");
        return;
      }

      // 2) 일부만 비어있음 → 비어있는 칸 각각에 표시
      if (v.commonErr === "모든 칸을 채워주세요.") {
        if (!username)    setUsernameError("아이디를 입력하세요.");
        if (!pwRaw)       setPwError("비밀번호를 입력하세요.");
        if (!phoneDigits) setPhoneError("전화번호를 입력하세요.");
        if (!name)        setNameError("이름을 입력하세요.");
        return;
      }

      // 3) 모두 채웠지만 상세 검증 실패
      if (v.pwErr)    setPwError(v.pwErr);
      if (v.phoneErr) setPhoneError(v.phoneErr);
      if (v.commonErr) setErrorMsg(v.commonErr);
      return;
    }

    try {
      setSaving(true); setErrorMsg("");
      const res = await fetch(apiUrl(`/api/staff`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const created = await res.json();
      setData((prev) => [...prev, created].sort((a, b) => a.id - b.id));
      setForm({ username: "", password: "", phone: "", name: "" });
      setPhoneError(""); setPwError(""); setUsernameError(""); setNameError(""); setErrorMsg(""); setIsAdding(false);
    } catch (e) {
      console.error(e);
      setErrorMsg("저장에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  /* ===== 렌더 ===== */
  return (
    <div className="admin-container staff-page">
      <h2 className="admin-title">직원 목록</h2>

      <div className="delete-button-container">
        <div className="button-row">
          <button className="toggle-button" onClick={handleToggleView}>
            {showDeleted ? "유효한 직원 보기" : "삭제된 직원 보기"}
          </button>

          {showDeleted ? (
            <>
              <span className="btn-wrap">
                <button className="restore-button" onClick={handleRestore}>복원</button>
                {warningFor === 'restore' && (
                  <div className="warning-tooltip">복원할 행을 체크하세요</div>
                )}
              </span>

              <span className="btn-wrap">
                <button className="purge-button" onClick={handleHardDelete}>직원 목록에서 삭제</button>
                {warningFor === 'hard' && (
                  <div className="warning-tooltip">영구 삭제할 행을 체크하세요</div>
                )}
              </span>
            </>
          ) : (
            <span className="btn-wrap">
              <button className="delete-button" onClick={handleDelete}>삭제</button>
              {warningFor === 'delete' && (
                <div className="warning-tooltip">삭제할 행을 체크하세요</div>
              )}
            </span>
          )}
        </div>
      </div>

      {/* ===== 데스크톱: 표 ===== */}
      <table className="admin-table">
        <thead>
          <tr>
            <th></th><th>번호</th><th>아이디</th><th>비밀번호</th><th>전화번호</th><th>이름</th>
          </tr>
        </thead>
        <tbody className={showDeleted ? "gray-out-rows" : ""}>
          {loading ? (
            <tr><td colSpan="6" className="error-message">불러오는 중…</td></tr>
          ) : rowsToRender.length ? (
            rowsToRender.map((row, idx) => (
              <tr key={row.id}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedRows.includes(row.id)}
                    onChange={() => handleCheckbox(row.id)}
                  />
                </td>
                <td className="number-cell">{startIdx + idx + 1}</td>
                <td>{isEmpty(row.username) ? "-" : row.username}</td>
                {/* 해시 그대로 표기 */}
                <td className="pw-cell">{isEmpty(row.password) ? "-" : row.password}</td>
                <td>{isEmpty(row.phone) ? "-" : maskPhone(row.phone)}</td>
                <td>{isEmpty(row.name) ? "-" : row.name}</td>
              </tr>
            ))
          ) : (
            <tr><td colSpan="6" className="error-message">
              {authExpired
                ? "세션이 만료되었습니다. 새로고침(F5) 후 다시 로그인해 주세요."
                : (baseRows.length ? "검색 조건에 맞는 직원이 없습니다." : "표시할 데이터가 없습니다.")}
            </td></tr>
          )}

          {!showDeleted && (!isAdding ? (
            <tr>
              <td colSpan="6">
                <button className="add-button" onClick={startAdd}>입력</button>
              </td>
            </tr>
          ) : (
            <tr className="add-row">
              <td></td><td className="number-cell">—</td>

              {/* 아이디 */}
              <td>
                <div className={`field-tip ${usernameError ? "show" : ""}`}>
                  <input name="username" value={form.username} onChange={onChange} placeholder="아이디" />
                  {usernameError && <span className="bubble">{usernameError}</span>}
                </div>
              </td>

              {/* 비밀번호 */}
              <td>
                <div className={`field-tip ${pwError ? "show" : ""}`}>
                  <input name="password" type="password" value={form.password} onChange={onChange} placeholder="비밀번호(8~20자)" maxLength={20} />
                  {pwError && <span className="bubble">{pwError}</span>}
                </div>
              </td>

              {/* 전화번호 */}
              <td>
                <div className={`field-tip ${phoneError ? "show" : ""}`}>
                  <input name="phone" value={form.phone} onChange={onChange} placeholder="전화번호(숫자/하이픈)" maxLength={20} />
                  {phoneError && <span className="bubble">{phoneError}</span>}
                </div>
              </td>

              {/* 이름 + 버튼들 */}
              <td>
                <div className="add-actions">
                  <div className={`field-tip ${nameError ? "show" : ""}`} style={{ marginRight: 8 }}>
                    <input name="name" value={form.name} onChange={onChange} placeholder="이름" />
                    {nameError && <span className="bubble">{nameError}</span>}
                  </div>
                    <span className={`field-tip field-tip--above ${submitTip ? "show" : ""}`} style={{ marginRight: 8 }}>
                    <button disabled={saving} onClick={submitAdd} className="confirm-button">
                      {saving ? "저장 중…" : "확인"}
                    </button>
                    {submitTip && <span className="bubble">{submitTip}</span>}
                    </span>
                  <button disabled={saving} onClick={cancelAdd} className="cancel-button">취소</button>
                </div>
                {errorMsg && !phoneError && <div className="error-message" style={{ marginTop: 6 }}>{errorMsg}</div>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* ===== 모바일: 2줄 카드형 ===== */}
      <div className={`staff-cards ${showDeleted ? "cards-deleted" : ""}`}>
        {loading ? (
          <div className="card-empty">불러오는 중…</div>
        ) : rowsToRender.length ? (
          <>
            {rowsToRender.map((row, idx) => (
              <article key={row.id} className="staff-card">
                <header className="card-top">
                  <input
                    type="checkbox"
                    checked={selectedRows.includes(row.id)}
                    onChange={() => handleCheckbox(row.id)}
                  />
                  <span className="no">번호 {startIdx + idx + 1}</span>
                </header>
                <dl className="kv">
                  <div><dt>아이디</dt><dd className="id">{isEmpty(row.username) ? "-" : row.username}</dd></div>
                  <div><dt>비밀번호</dt><dd className="pw">{isEmpty(row.password) ? "-" : row.password}</dd></div>
                  <div><dt>전화번호</dt><dd className="phone">{isEmpty(row.phone) ? "-" : maskPhone(row.phone)}</dd></div>
                  <div><dt>이름</dt><dd>{isEmpty(row.name) ? "-" : row.name}</dd></div>
                </dl>
              </article>
            ))}

            {!showDeleted && (
              !isAdding ? (
                <button className="add-button mobile-add-button" onClick={startAdd}>입력</button>
              ) : (
                <article className="staff-card add-card">
                  <header className="card-top"><span className="no">신규 입력</span></header>
                  <dl className="kv">
                  <div>
                    <dt>아이디</dt>
                    <dd>
                      <input name="username" value={form.username} onChange={onChange} placeholder="아이디" />
                      {usernameError && <div className="error-message input-error">{usernameError}</div>}
                    </dd>
                  </div>

                  <div>
                    <dt>비밀번호</dt>
                    <dd>
                      <input name="password" value={form.password} onChange={onChange} placeholder="비밀번호" type="password" />
                      {pwError && <div className="error-message input-error">{pwError}</div>}
                    </dd>
                  </div>

                  <div>
                    <dt>전화번호</dt>
                    <dd>
                      <input name="phone" value={form.phone} onChange={onChange} placeholder="전화번호(숫자/하이픈)" />
                      {phoneError && <div className="error-message input-error">{phoneError}</div>}
                    </dd>
                  </div>

                  <div>
                    <dt>이름</dt>
                    <dd>
                      <input name="name" value={form.name} onChange={onChange} placeholder="이름" />
                      {nameError && <div className="error-message input-error">{nameError}</div>}
                    </dd>
                  </div>
                </dl>
                  {errorMsg && <div className="error-message" style={{ marginTop: 6 }}>{errorMsg}</div>}
                  <div className="add-actions">
                  {submitTip && <div className="error-message inline-error">{submitTip}</div>}
                  <button disabled={saving} onClick={submitAdd} className="confirm-button">
                    {saving ? "저장 중…" : "확인"}
                  </button>
                  <button disabled={saving} onClick={cancelAdd} className="cancel-button">취소</button>
                </div>
                </article>
              )
            )}
          </>
        ) : (
          <div className="card-empty">
            {authExpired
              ? "세션이 만료되었습니다. 새로고침(F5) 후 다시 로그인해 주세요."
              : (baseRows.length ? "검색 조건에 맞는 직원이 없습니다." : "표시할 데이터가 없습니다.")}
          </div>
        )}
      </div>

      {/* ===== 하단: 엑셀 + 페이지네이션 한 줄 ===== */}
      <div className="bottom-row">
        <button className="download-button" onClick={handleDownload}>엑셀 다운로드</button>
        <Pagination page={page} totalPages={totalPages} total={total} setPage={setPage} />
      </div>

      {/* ===== 표 아래 중앙: 검색 카드 ===== */}
      <div className="search-card">
        <form className="search-form search-form--staff" onSubmit={onSubmitSearch}>
          <input
            placeholder="아이디"
            value={draft.username}
            onChange={e=>setDraft({...draft, username:e.target.value})}
            ref={(el) => (searchRefs.current[0] = el)}
            onKeyDown={(e)=>handleArrowNav(e, 0)}
          />
          <input
            placeholder="비밀번호"
            value={draft.password}
            onChange={e=>setDraft({...draft, password:e.target.value})}
            ref={(el) => (searchRefs.current[1] = el)}
            onKeyDown={(e)=>handleArrowNav(e, 1)}
          />
          <input
            placeholder="전화번호"
            value={draft.phone}
            onChange={(e)=>setDraft({...draft, phone: sanitizePhone(e.target.value)})}
            ref={(el) => (searchRefs.current[2] = el)}
            onKeyDown={(e)=>handleArrowNav(e, 2)}
          />
          <input
            placeholder="이름"
            value={draft.name}
            onChange={e=>setDraft({...draft, name:e.target.value})}
            ref={(el) => (searchRefs.current[3] = el)}
            onKeyDown={(e)=>handleArrowNav(e, 3)}
          />

          {/* 검색은 submit, 나머지는 button 으로 타입 지정! */}
          <button type="submit" className="btn-search">검색</button>
          <button type="button" className="btn-reset" onClick={clearSearch}>취소</button>
          <button type="button" className="btn-showall" onClick={showAll} aria-label="전체 보기" title="전체 보기" />
        </form>
      </div>

      {/* 다운로드 상태 모달 */}
      <Modal
        open={modal.open}
        title={modal.title}
        message={modal.message}
        loading={modal.loading}
        danger={modal.danger}
        closable={modal.closable}
        onClose={() => setModal({ open: false, title: "", message: "", loading: false, danger: false, closable: true })}
      />

      {/* 커스텀 확인 모달 */}
      {ConfirmDialog}
    </div>
  );
}
